import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { StatCard } from '../../components/ui/StatCard';
import { Progress } from '../../components/ui/Progress';
import { StatusBadge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { cn } from '../../lib/utils';
import { apiService } from '../../services/api';
import { formatDateTime } from '../../utils/timezoneUtils';

interface DashboardStats {
  total_requests: number;
  completed_requests: number;
  in_progress_requests: number;
  rejected_requests: number;
  average_progress: number;
  success_rate: number;
  paid_requests: number;
  pending_payments: number;
  total_amount_due: number;
  total_paid_amount: number;
}

interface RecentRequest {
  id: number;
  request_number: string;
  status: string;
  progress: number;
  company_name: string;
  country: string;
  country_flag: string;
  package_name: string;
  created_at: string;
  status_badge: {
    color: string;
    text: string;
  };
  progress_percentage?: number;
  payment_status?: string;
  package_price?: number;
  package_currency?: string;
  total_amount?: number;
  paid_amount?: number;
  documents?: any[];
  status_history?: any[];
}

interface DashboardData {
  stats: DashboardStats;
  recent_requests: RecentRequest[];
  status_distribution: Array<{ status: string; count: number }>;
  monthly_trend: Array<{ month: string; count: number }>;
}

const Overview: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<RecentRequest | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const result = await apiService.getDashboardStats();
      setDashboardData(result);
      setError(null);
    } catch (error) {      // Don't set error for authentication issues - apiService handles redirects
      if (error instanceof Error && error.message.includes('Session expired')) {
        // Don't set error state - user will be redirected to login
        return;
      }
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColorClasses = (color: string) => {
    const classes: Record<string, string> = {
      gray: 'bg-gray-100 text-gray-800',
      blue: 'bg-blue-100 text-blue-800',
      yellow: 'bg-yellow-100 text-yellow-800',
      orange: 'bg-orange-100 text-orange-800',
      purple: 'bg-purple-100 text-purple-800',
      indigo: 'bg-indigo-100 text-indigo-800',
      green: 'bg-green-100 text-green-800',
      red: 'bg-red-100 text-red-800'
    };
    return classes[color] || 'bg-gray-100 text-gray-800';
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { color: string; key: string }> = {
      'draft': { color: 'gray', key: 'draft' },
      'submitted': { color: 'blue', key: 'submitted' },
      'processing': { color: 'yellow', key: 'processing' },
      'in_review': { color: 'orange', key: 'inReview' },
      'documents_requested': { color: 'purple', key: 'documentsRequested' },
      'documents_received': { color: 'indigo', key: 'documentsReceived' },
      'under_review': { color: 'orange', key: 'underReview' },
      'approved': { color: 'green', key: 'approved' },
      'completed': { color: 'green', key: 'completed' },
      'rejected': { color: 'red', key: 'rejected' },
      'cancelled': { color: 'gray', key: 'cancelled' },
      'pending_docs': { color: 'purple', key: 'pendingDocs' }
    };

    const statusInfo = statusMap[status] || { color: 'gray', key: '' };
    const text = statusInfo.key ? t(`dashboard.statusLabels.${statusInfo.key}`) : status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());

    return { color: statusInfo.color, text };
  };

  const formatDateTimeDisplay = (dateString: string) => {
    const locale = i18n.language === 'ar' ? 'ar' : 'en-US';
    return formatDateTime(dateString, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }, locale);
  };

  const getCountryFlagImage = (countryFlag: string, countryName: string) => {
    const isEmoji = countryFlag && countryFlag.length > 2;
    const fallbackEmoji = isEmoji ? countryFlag : '🌍';
    const countryCode = isEmoji ? '' : countryFlag.toUpperCase();

    if (countryCode && countryCode.length <= 3) {
      return (
        <img
          src={'https://flagcdn.com/48x36/' + countryCode.toLowerCase() + '.png'}
          alt={`${countryName} flag`}
          className="w-6 h-4 object-cover rounded-sm"
          onError={(e) => {
            const target = e.currentTarget;
            target.style.display = 'none';
            const parent = target.parentNode as HTMLElement;
            if (parent && !parent.querySelector('.flag-fallback')) {
              const span = document.createElement('span');
              span.className = 'flag-fallback text-xl';
              span.textContent = fallbackEmoji;
              parent.appendChild(span);
            }
          }}
        />
      );
    }

    return <span className="text-xl">{fallbackEmoji}</span>;
  };

  const handleCreateNewRequest = () => {
    navigate('/dashboard/create-request');
  };

  const handleViewDetails = async (request: RecentRequest) => {
    setSelectedRequest(request);
    setShowDetails(true);
    setDetailsLoading(true);

    try {
      // Fetch detailed request data including documents and status history
      const result = await apiService.getRequestDetails(request.id);

      // Map the detailed request to RecentRequest format
      const mappedRequest: RecentRequest = {
        ...result.request,
        progress: result.request.progress_percentage || 0,
        country: result.request.country_name || '',
        status_badge: getStatusBadge(result.request.status)
      };
      setSelectedRequest(mappedRequest);
    } catch (error) {    } finally {
      setDetailsLoading(false);
    }
  };

  const handleViewAllRequests = () => {
    navigate('/dashboard/requests');
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-500 text-lg mb-2">⚠️</div>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={fetchDashboardData}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
          >
            {t('profile.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-600">{t('errors.failedToLoad')} dashboard data</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      <div className="mb-8">
        <motion.h1
          className="text-3xl font-bold text-neutral-900 mb-2"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
        >
          {t('dashboard.welcomeBack')}
        </motion.h1>
        <motion.p
          className="text-neutral-600 text-lg"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
        >
          {t('dashboard.journeyOverview')}
        </motion.p>
      </div>

      {/* Enhanced Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6 mb-8">
        <StatCard
          title={t('dashboard.totalRequests')}
          value={dashboardData.stats.total_requests}
          icon="📊"
          variant="primary"
          description={t('dashboard.allCompanyRequests')}
        />
        <StatCard
          title={t('dashboard.inProgress')}
          value={dashboardData.stats.in_progress_requests}
          icon="⚙️"
          variant="warning"
          description={t('dashboard.currentlyBeingProcessed')}
          trend={{
            value: 12,
            label: t('dashboard.thisMonth'),
            isPositive: true
          }}
        />
        <StatCard
          title={t('dashboard.completed')}
          value={dashboardData.stats.completed_requests}
          icon="🎉"
          variant="success"
          description={t('dashboard.successfullyCompleted')}
        />
        <StatCard
          title={t('dashboard.successRate')}
          value={`${Math.round(dashboardData.stats.success_rate || 0)}%`}
          icon="📈"
          variant="default"
          description={t('dashboard.overallCompletionRate')}
        />
      </div>

      {/* Quick Actions */}
      <motion.div
        className="mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card variant="gradient" className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-4 sm:space-y-0">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">{t('dashboard.readyToStartNew')}</h3>
              <p className="text-neutral-600">{t('dashboard.streamlinedProcess')}</p>
            </div>
            <Button
              size="lg"
              gradient
              onClick={handleCreateNewRequest}
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
              }
            >
              {t('dashboard.createNewRequest')}
            </Button>
          </div>
        </Card>
      </motion.div>


      {/* Recent Requests */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <Card variant="elevated" className="overflow-hidden">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>{t('dashboard.recentRequests')}</CardTitle>
              <Button variant="ghost" size="sm" onClick={handleViewAllRequests}>
                {t('dashboard.viewAll')}
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </Button>
            </div>
          </CardHeader>

          {dashboardData.recent_requests.length === 0 ? (
            <CardContent>
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 text-neutral-400">
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                    <path fillRule="evenodd" d="M4 5a2 2 0 012-2v1a1 1 0 102 0V3h4v1a1 1 0 102 0V3a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm2.5 7a1.5 1.5 0 100-3 1.5 1.5 0 000 3zm2.45-1.5a2.5 2.5 0 10-3.9 0 .5.5 0 00-.05.5c0 .28.22.5.5.5h3a.5.5 0 00.5-.5.5.5 0 00-.05-.5z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-neutral-900 mb-2">{t('dashboard.noRequestsYet')}</h3>
                <p className="text-neutral-600 mb-6">{t('dashboard.createFirstMessage')}</p>
                <Button size="lg" gradient onClick={handleCreateNewRequest}>
                  {t('dashboard.createFirstRequest')}
                </Button>
              </div>
            </CardContent>
          ) : (
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[350px] md:min-w-[600px]">
                  <thead className="bg-neutral-50/50 border-b border-neutral-200">
                    <tr>
                      <th className="px-6 py-4 text-left rtl:text-right text-xs font-semibold text-neutral-600 uppercase tracking-wider w-1/4">
                        {t('dashboard.request')}
                      </th>
                      <th className="px-6 py-4 text-left rtl:text-right text-xs font-semibold text-neutral-600 uppercase tracking-wider w-1/4">
                        {t('dashboard.country')}
                      </th>
                      <th className="px-6 py-4 text-left rtl:text-right text-xs font-semibold text-neutral-600 uppercase tracking-wider w-1/6">
                        {t('dashboard.status')}
                      </th>
                      <th className="px-6 py-4 text-left rtl:text-right text-xs font-semibold text-neutral-600 uppercase tracking-wider w-1/6">
                        {t('dashboard.progress')}
                      </th>
                      <th className="px-6 py-4 text-left rtl:text-right text-xs font-semibold text-neutral-600 uppercase tracking-wider w-1/6">
                        {t('dashboard.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {dashboardData.recent_requests.map((request, index) => (
                      <motion.tr
                        key={request.id}
                        className="hover:bg-neutral-50/50 transition-colors duration-150"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 * index }}
                      >
                        <td className="px-6 py-4 text-left rtl:text-right">
                          <div>
                            <div className="text-sm font-semibold text-neutral-900">
                              {request.request_number}
                            </div>
                            <div className="text-xs text-neutral-600">
                              {request.package_name}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-left rtl:text-right">
                          <div className="flex items-center">
                            <span className="mr-3 rtl:ml-3 rtl:mr-0">
                              {getCountryFlagImage(request.country_flag, request.country)}
                            </span>
                            <span className="text-sm font-medium text-neutral-700">
                              {request.country}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-left rtl:text-right">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColorClasses(getStatusBadge(request.status).color)}`}>
                            {getStatusBadge(request.status).text}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-left rtl:text-right">
                          <div className="w-24">
                            <Progress
                              value={request.progress}
                              size="sm"
                              showPercentage
                              className="mb-1"
                            />
                          </div>
                        </td>
                        <td className="px-6 py-4 text-left rtl:text-right">
                          <div className="flex items-center space-x-2 rtl:space-x-reverse">
                            <Button variant="ghost" size="xs" onClick={() => handleViewDetails(request)}>
                              {t('dashboard.viewDetails')}
                            </Button>
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          )}
        </Card>
      </motion.div>

      {/* Request Details Modal */}
      {showDetails && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden shadow-2xl mx-2 sm:mx-0">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b px-4 sm:px-6 py-4 flex justify-between items-start">
              <div className="flex-1 min-w-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
                  {selectedRequest.request_number} - {selectedRequest.company_name}
                </h2>
                <p className="text-sm text-gray-600 mt-1 flex items-center">
                  <span className="mr-2 rtl:ml-2 rtl:mr-0">
                    {getCountryFlagImage(selectedRequest.country_flag, selectedRequest.country)}
                  </span>
                  <span className="truncate">{selectedRequest.country} • {selectedRequest.package_name}</span>
                </p>
              </div>
              <button
                onClick={() => setShowDetails(false)}
                className="flex-shrink-0 ml-4 text-gray-400 hover:text-gray-600 transition-colors p-1"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="overflow-y-auto max-h-[calc(95vh-140px)] sm:max-h-[calc(90vh-140px)] p-4 sm:p-6 bg-gray-50">
              {detailsLoading ? (
                <div className="flex justify-center items-center py-12">
                  <LoadingSpinner />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Status and Payment */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
                      <h3 className="text-lg font-medium mb-3 text-gray-900">{t('dashboard.requestStatus')}</h3>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColorClasses(getStatusBadge(selectedRequest.status).color)}`}>
                        {getStatusBadge(selectedRequest.status).text}
                      </span>
                      <div className="mt-4">
                        <div className="flex justify-between text-sm text-gray-600 mb-1">
                          <span>{t('dashboard.progress')}</span>
                          <span>{selectedRequest.progress_percentage || selectedRequest.progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                            style={{ width: `${selectedRequest.progress_percentage || selectedRequest.progress}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
                      <h3 className="text-lg font-medium mb-3 text-gray-900">{t('dashboard.paymentStatus')}</h3>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        selectedRequest.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {selectedRequest.payment_status === 'paid' ? `✓ ${t('dashboard.paid')}` : `⏳ ${t('dashboard.paymentPending')}`}
                      </span>
                      <p className="text-sm text-gray-600 mt-2">
                        {t('dashboard.amount')}: ${selectedRequest.total_amount || selectedRequest.package_price} {selectedRequest.package_currency}
                      </p>
                    </div>
                  </div>

                  {/* Documents Section */}
                  {selectedRequest.documents && selectedRequest.documents.length > 0 && (
                    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">{t('dashboard.documents')}</h3>
                      <div className="space-y-3">
                        {selectedRequest.documents.map((doc: any) => (
                          <div key={doc.id} className="border rounded-lg p-4 hover:bg-gray-50">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-3">
                                  <div className="text-2xl">
                                    {doc.mime_type?.includes('image') ? '🖼️' :
                                     doc.mime_type?.includes('pdf') ? '📄' : '📎'}
                                  </div>
                                  <div>
                                    <h4 className="font-medium text-gray-900">{doc.original_filename}</h4>
                                    <p className="text-sm text-gray-600">
                                      {doc.document_type.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                                      • {(doc.file_size / 1024).toFixed(1)} KB
                                      • {t('dashboard.uploaded')} {formatDateTimeDisplay(doc.uploaded_at)}
                                    </p>
                                  </div>
                                </div>
                              </div>
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                doc.status === 'approved' ? 'bg-green-100 text-green-800' :
                                doc.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Status History Section */}
                  {selectedRequest.status_history && selectedRequest.status_history.length > 0 && (
                    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">{t('dashboard.statusHistory')}</h3>
                      <div className="relative">
                        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-300"></div>
                        <div className="space-y-6">
                          {selectedRequest.status_history.map((history: any, index: number) => (
                            <div key={history.id} className="relative flex items-start space-x-4">
                              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                                index === 0 ? 'bg-blue-600' : 'bg-gray-400'
                              }`}>
                                <div className="w-2 h-2 bg-white rounded-full"></div>
                              </div>
                              <div className="flex-1 bg-gray-50 border rounded-lg p-4">
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="font-medium text-gray-900">{history.new_status}</h4>
                                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                                    <span>{formatDateTimeDisplay(history.created_at)}</span>
                                  </div>
                                </div>
                                {history.comment && (
                                  <p className="text-gray-700 mb-2">{history.comment}</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* View Full Details Link */}
                  <div className="text-center">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowDetails(false);
                        navigate(`/dashboard/requests?request=${selectedRequest.request_number}`);
                      }}
                    >
                      {t('dashboard.viewFullDetails')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default Overview;